import html2canvas from 'html2canvas'
import { nextTick } from 'vue'
import NoPreviewAvailable from '../assets/no-preview-available.svg?raw'
import { ELEMENTS_LOOKUP } from '../const'
import { IElement } from '../types'

export const useHtmlToCanvasFactory = () => {
  const waitForNextFrame = () => {
    return new Promise(resolve => requestAnimationFrame(resolve))
  }

  const waitForImageLoad = img => {
    return new Promise((resolve, reject) => {
      img.onload = () => resolve()
      img.onerror = err => reject(err)
    })
  }

  const generateThumbnailWithElement = async (elem: HTMLElement) => {
    const canvas = await html2canvas(elem, {
      scale: 0.3,
      useCORS: true,
      allowTaint: true,
      logging: false,
    })

    const base64Image = canvas.toDataURL('image/jpeg', 1)
    return base64Image
  }

  const extractVideoId = (url: string) => {
    // YouTube
    if (url.includes('youtube.com') || url.includes('youtu.be')) {
      const match = url.match(
        /(?:youtube\.com\/(?:.*v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]+)/
      )
      return match ? { id: match[1], type: 'youtube' } : null
    }

    // Vimeo
    if (url.includes('vimeo.com')) {
      const match = url.match(/(?:vimeo\.com\/|video\/)(\d+)/)
      return match ? { id: match[1], type: 'vimeo' } : null
    }

    // Dailymotion
    if (url.includes('dailymotion.com') || url.includes('dai.ly')) {
      const match = url.match(
        /(?:dailymotion\.com\/video\/|dai\.ly\/)([a-zA-Z0-9]+)/
      )
      return match ? { id: match[1], type: 'dailymotion' } : null
    }

    // Wistia
    if (url.includes('wistia.com') || url.includes('wistia.net')) {
      const match = url.match(/(?:medias|iframe)\/([a-zA-Z0-9]+)/)
      return match ? { id: match[1], type: 'wistia' } : null
    }

    // Vidyard
    if (url.includes('vidyard.com')) {
      const match = url.match(/vidyard\.com\/(?:watch\/|v\/)?([a-zA-Z0-9]+)/)
      return match ? { id: match[1], type: 'vidyard' } : null
    }

    // Loom
    if (url.includes('loom.com')) {
      const match = url.match(/loom\.com\/(?:share|embed)\/([a-zA-Z0-9]+)/)
      return match ? { id: match[1], type: 'loom' } : null
    }

    return null
  }

  const getVideoThumbnail = async iframe => {
    const url = iframe.src
    const videoData = extractVideoId(url)
    if (!videoData) return ''

    const { id, type } = videoData

    switch (type) {
      case 'youtube':
        try {
          return `https://img.youtube.com/vi/${id}/hqdefault.jpg`
        } catch (error) {
          console.error('Error fetching Youtube thumbnail:', error)
          return ''
        }

      case 'vimeo':
        try {
          const response = await fetch(
            `https://vimeo.com/api/v2/video/${id}.json`
          )
          const data = await response.json()
          return data[0]?.thumbnail_large || ''
        } catch (error) {
          console.error('Error fetching Vimeo thumbnail:', error)
          return ''
        }

      case 'dailymotion':
        try {
          return `https://www.dailymotion.com/thumbnail/video/${id}`
        } catch (error) {
          console.error('Error fetching DailyMotion thumbnail:', error)
          return ''
        }

      case 'wistia':
        try {
          const response = await fetch(
            `https://fast.wistia.com/oembed?url=https://home.wistia.com/medias/${id}`
          )
          const data = await response.json()
          return (
            data.thumbnail_url ||
            `https://fast.wistia.com/embed/medias/${id}.jpg`
          )
        } catch (error) {
          console.error('Error fetching Wistia thumbnail:', error)
          return `https://fast.wistia.com/embed/medias/${id}.jpg`
        }

      case 'vidyard':
        try {
          return `https://play.vidyard.com/${id}.jpg`
        } catch (error) {
          console.error('Error fetching Vidyard thumbnail:', error)
          return ''
        }

      case 'loom':
        try {
          const oembedUrl = new URL('https://www.loom.com/oembed')
          oembedUrl.searchParams.set('url', `https://www.loom.com/share/${id}`)
          const resp = await fetch(oembedUrl.toString())
          if (resp.ok) {
            const json = await resp.json()
            if (json.thumbnail_url) {
              return json.thumbnail_url
            }
          }
          return ''
        } catch (error) {
          console.error('Error fetching loom thumbnail:', error)
          return ''
        }

      default:
        return ''
    }
  }

  const getPageWrapper = async (base64Image: string) => {
    const hiddenDiv = document.createElement('div')
    hiddenDiv.style.position = 'absolute'
    hiddenDiv.style.left = '-9999px'
    hiddenDiv.style.overflow = 'hidden'
    hiddenDiv.style.background = 'white'
    document.body.appendChild(hiddenDiv)
    hiddenDiv.innerHTML = `
                  <div style="width: 816px; height: 1056px;
                    margin: 48px; display: flex;
                    flex-direction: column; align-items: center;
                    justify-content: flex-start; overflow: hidden;">
                    <img src="${base64Image}" style="width: 100%; height: auto; object-fit: contain;" />
                  </div>
                `
    try {
      const finalCanvas = await html2canvas(hiddenDiv, { scale: 0.3 })
      const finalBase64 = finalCanvas.toDataURL('image/jpeg', 0.6)
      return finalBase64
    } catch (error) {
      console.error('Error capturing thumbnail:', error)
    } finally {
      document.body.removeChild(hiddenDiv)
    }
    return
  }

  const captureVideoElement = async (elem: HTMLElement) => {
    if (elem) {
      const clonedElem = elem.cloneNode(true)
      Object.assign(clonedElem.style, {
        position: 'absolute',
        top: '0px',
        left: '-9999px',
        visibility: 'visible',
        opacity: '1',
        width: elem.offsetWidth + 'px',
        height: elem.offsetHeight + 'px',
      })
      elem.insertAdjacentElement('afterend', clonedElem)
      await waitForNextFrame()
      const iframe = clonedElem.querySelector('iframe')
      const svgDataUrl = `data:image/svg+xml;utf8,${encodeURIComponent(
        NoPreviewAvailable.trim()
      )}`
      if (iframe) {
        iframe.style.width = iframe.offsetWidth
          ? `${iframe.offsetWidth}px`
          : '100%'
        iframe.style.height = iframe.offsetHeight
          ? `${iframe.offsetHeight}px`
          : '100%'
        const thumbnailUrl = await getVideoThumbnail(iframe)
        const img = document.createElement('img')

        img.src = thumbnailUrl || svgDataUrl
        img.style.width = iframe.offsetWidth + 'px'
        img.style.height = iframe.offsetHeight + 'px'
        iframe.replaceWith(img)
        await waitForImageLoad(img)
        await new Promise(resolve => setTimeout(resolve, 100))
        await nextTick()
        const canvas = await html2canvas(clonedElem, {
          scale: 0.3,
          useCORS: true,
        })
        const base64Image = canvas.toDataURL('image/jpeg', 0.8)
        if (clonedElem?.remove) clonedElem?.remove()
        const finalImage = await getPageWrapper(base64Image)
        return finalImage
      } else {
        const vidyardImg = clonedElem.querySelector(
          'img.vidyard-player-embed'
        ) as HTMLImageElement
        if (vidyardImg) {
          vidyardImg.style.width = vidyardImg.offsetWidth
            ? `${vidyardImg.offsetWidth}px`
            : '100%'
          vidyardImg.style.height = vidyardImg.offsetHeight
            ? `${vidyardImg.offsetHeight}px`
            : '100%'
          const fallbackImage = document.createElement('img')
          fallbackImage.src = vidyardImg.src || svgDataUrl
          vidyardImg.replaceWith(fallbackImage)
          await waitForImageLoad(fallbackImage)
        } else {
          const fallbackImage = document.createElement('img')
          fallbackImage.src = svgDataUrl
          fallbackImage.style.width = elem.offsetWidth + 'px'
          fallbackImage.style.height = elem.offsetHeight + 'px'
          clonedElem.appendChild(fallbackImage)
          await waitForImageLoad(fallbackImage)
        }
        await new Promise(resolve => setTimeout(resolve, 100))
        await nextTick()
        const canvas = await html2canvas(clonedElem, {
          scale: 0.3,
          useCORS: true,
        })
        const base64Image = canvas.toDataURL('image/jpeg', 0.8)
        if (clonedElem?.remove) clonedElem?.remove()
        const finalImage = await getPageWrapper(base64Image)
        return finalImage
      }
    }
  }

  const generateHiddenThumbnail = async (element: IElement) => {
    const elementId = element.id
    const elem = document.querySelector(`[data-id="${elementId}"]`)
    if (!elem) return

    if (element?.type && element.type === ELEMENTS_LOOKUP.VIDEO) {
      const videoImage = await captureVideoElement(elem as HTMLElement)
      return videoImage
    }
    const canvas = await html2canvas(elem, {
      scale: 0.3,
      useCORS: true,
    })
    await new Promise(resolve => setTimeout(resolve, 200))
    await nextTick()
    const base64Image = canvas.toDataURL('image/jpeg', 0.8)
    const finalImage = await getPageWrapper(base64Image)
    return finalImage
  }

  const generateThumbnailForPageHasVideoElements = async (
    elem: HTMLElement
  ) => {
    if (elem) {
      const clonedElem = elem.cloneNode(true)
      Object.assign(clonedElem.style, {
        position: 'absolute',
        top: '0px',
        left: '-9999px',
        visibility: 'visible',
        opacity: '1',
        width: elem.offsetWidth + 'px',
        height: elem.offsetHeight + 'px',
      })
      elem.insertAdjacentElement('afterend', clonedElem)
      await waitForNextFrame()
      const videoElements = clonedElem.querySelectorAll('.video-el')
      for (const videoElement of videoElements) {
        const iframe = videoElement.querySelector('iframe')
        iframe.style.width = iframe.offsetWidth
          ? `${iframe.offsetWidth}px`
          : '100%'
        iframe.style.height = iframe.offsetHeight
          ? `${iframe.offsetHeight}px`
          : '100%'
        if (iframe) {
          const thumbnailUrl = await getVideoThumbnail(iframe)
          if (thumbnailUrl) {
            const img = document.createElement('img')
            img.src = thumbnailUrl
            img.style.width = iframe.offsetWidth + 'px'
            img.style.height = iframe.offsetHeight + 'px'
            iframe.replaceWith(img)
            await waitForImageLoad(img)
          }
        }
      }
      await new Promise(resolve => setTimeout(resolve, 200))
      await nextTick()
      const canvas = await html2canvas(clonedElem, {
        scale: 0.3,
        useCORS: true,
      })
      const base64Image = canvas.toDataURL('image/jpeg', 0.8)
      clonedElem.remove()
      const finalImage = await getPageWrapper(base64Image)
      return finalImage
    }
  }

  const generateThumbnailForPage = async (pageId: string) => {
    const element = document.getElementById(`page-${pageId}`)
    if (!element) return
    if (element) {
      try {
        const videoElements = element.querySelectorAll('.video-el')
        if (videoElements.length > 0) {
          const finalImage = await generateThumbnailForPageHasVideoElements(
            element
          )
          return finalImage
        }
      } catch (error) {
        console.error('Error generating thumbnail:', error)
      }
    }
    try {
      const base64Image = await generateThumbnailWithElement(element)
      return base64Image
    } catch (error) {
      console.error('Error generating thumbnail:', error)
    }
    return
  }

  return {
    getPageWrapper,
    getVideoThumbnail,
    generateThumbnailWithElement,
    captureVideoElement,
    generateThumbnailForPageHasVideoElements,
    generateHiddenThumbnail,
    generateThumbnailForPage,
  }
}
